/* eslint-disable react/prop-types */
// components/AttachmentsList.jsx
import { useState } from 'react';
import {
    File,
    FileText,
    FileImage,
    FileVideo,
    FileAudio,
    Download,
    Trash2,
    Eye,
    Grid3X3,
    List,
    Search,
    X
} from 'lucide-react';
import BuscadorInput from "../../../../../../Store/BuscadorInput.jsx";
import { showAlert, confirmDelete } from '/src/components/HelperAlerts/HelpersAlerts.jsx';
import {getThemeContext} from "../../../../../../../components/providers/ThemeProvider.jsx";
import getMediaUrl from "../../../../../../../components/tools/utils.jsx";

const AttachmentsList = ({
                             attachments,
                             handleDeleteAttachment,
                         }) => {
    const [viewMode, setViewMode] = useState('carousel');
    const [searchTerm, setSearchTerm] = useState('');
    const [previewFile, setPreviewFile] = useState(null);

    const isImageExt = (ext) =>
        ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext?.toLowerCase());

    const getIcon = (ext) => {
        switch (ext?.toLowerCase()) {
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'svg':
            case 'webp':
                return FileImage;
            case 'pdf':
                return FileText;
            case 'zip':
            case 'rar':
                return FileText;
            case 'mp4':
            case 'mov':
            case 'avi':
                return FileVideo;
            case 'mp3':
            case 'wav':
                return FileAudio;
            case 'txt':
            case 'md':
                return FileText;
            default:
                return File;
        }
    };

    const getFileSize = () => {
        // Mock file size for visual enhancement
        return (Math.random() * 10 + 0.5).toFixed(1) + ' MB';
    };

    const filteredAttachments = attachments?.filter(att => {
        const filename = att.file.split('/').pop();
        return filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
            att.descripcion?.toLowerCase().includes(searchTerm.toLowerCase());
    }) || [];

    const theme = getThemeContext()
    const onDeleteClick = async (id) => {
        const confirmed = await confirmDelete({
            title: 'Eliminar archivo',
            text: '¿Estás seguro de que deseas eliminar este adjunto?',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            theme: theme
        });
        if (!confirmed) return;
        handleDeleteAttachment(id);
        showAlert({ title: 'Adjunto eliminado', icon: 'success' });
    };

    return (
        <div className=" rounded-xl shadow-lg gap-3 flex flex-col ">
            {/* Header */}
            <div className="flex items-center justify-between space-x-3">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <FileImage className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Archivos Adjuntos</h3>
                        <p className="text-sm text-gray-500">{filteredAttachments.length} archivos</p>
                    </div>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1  w-full max-w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <BuscadorInput
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        className={"lg:w-96 "}
                    />
                </div>

                <div className="flex flex-shrink-0 items-center space-x-2">
                    <button
                        onClick={() => setViewMode('carousel')}
                        className={`btnOutline font-medium transition-all ${
                            viewMode === 'carousel'
                                ? ''
                                : ' bg-red-500'
                        }`}
                    >
                        <Grid3X3 className="w-4 h-4 mr-2" />
                        Carrusel
                    </button>
                    <button
                        onClick={() => setViewMode('table')}
                        className={` btnOutline font-medium transition-all ${
                            viewMode === 'table'
                                ? ''
                                : ' bg-red-400'
                        }`}
                    >
                        <List className="w-4 h-4 mr-2" />
                        Tabla
                    </button>
                </div>
            </div>

            {/* Content Container */}
            <div className="bg-base-300 rounded-xl p-4">
                {viewMode === 'carousel' ? (
                    <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        {filteredAttachments.map(att => {
                            const filename = att.file.split('/').pop();
                            const fileUrl = getMediaUrl(att.file);
                            const ext = filename.split('.').pop()?.toLowerCase();
                            const IconComponent = getIcon(ext);
                            const img = isImageExt(ext);

                            return (
                                <div
                                    key={att.id}
                                    className="relative group bg-base-100 rounded-xl shadow-sm  w-64 shrink-0 overflow-hidden hover:shadow-md transition-all duration-300"
                                >
                                    {img ? (
                                        <div className="relative">
                                            <img
                                                src={fileUrl}
                                                alt={filename}
                                                className="w-full h-40 object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                                <button
                                                    onClick={() => setPreviewFile(att)}
                                                    className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-lg transform scale-90 group-hover:scale-100 transition-all duration-300"
                                                >
                                                    <Eye className="w-4 h-4 text-gray-700" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-40 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                                            <IconComponent className="w-12 h-12 text-gray-400" />
                                        </div>
                                    )}

                                    <div className="p-4">
                                        <h4 className="font-medium text-gray-900 truncate mb-1">{filename}</h4>
                                        <p className="text-sm text-gray-500 mb-2">{getFileSize()}</p>
                                        {att.descripcion && (
                                            <p className="text-xs text-neutral-500 bg-base-200 px-2 py-1 rounded mb-3 line-clamp-2">
                                                {att.descripcion}
                                            </p>
                                        )}

                                        <div className="flex items-center space-x-2">
                                            <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 flex items-center justify-center px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                                            >
                                                <Download className="w-3 h-3 mr-1" />
                                                Abrir
                                            </a>
                                            <button
                                                onClick={() => onDeleteClick(att.id)}
                                                className="btn btn-sm btn-error"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900">Archivo</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900">Tamaño</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900">Descripción</th>
                                    <th className="text-right py-3 px-4 font-medium text-gray-900">Acciones</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                {filteredAttachments.map(att => {
                                    const filename = att.file.split('/').pop();
                                    const fileUrl = getMediaUrl(att.file);
                                    const ext = filename.split('.').pop()?.toLowerCase();
                                    const IconComponent = getIcon(ext);
                                    const img = isImageExt(ext);

                                    return (
                                        <tr key={att.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-4 px-4">
                                                <div className="flex items-center space-x-3">
                                                    {img ? (
                                                        <img
                                                            src={fileUrl}
                                                            alt={filename}
                                                            className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                                            <IconComponent className="w-5 h-5 text-gray-500" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <a
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                                                        >
                                                            {filename}
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-sm text-gray-500">{getFileSize()}</td>
                                            <td className="py-4 px-4 text-sm text-gray-600">
                                                {att.descripcion || <span className="text-gray-400">Sin descripción</span>}
                                            </td>
                                            <td className="py-4 px-4 text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <a
                                                        href={fileUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Abrir"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </a>
                                                    <button
                                                        onClick={() => onDeleteClick(att.id)}
                                                        className="btn btn-sm btn-error"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {previewFile && (
                <div className="fixed inset-0 bg-base-300 bg-opacity-90 flex items-center justify-center z-50 p-4 h-screen">
                    <div className="bg-base-100 rounded-xl max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="font-semibold text-gray-900">
                                {previewFile.file.split('/').pop()}
                            </h3>
                            <button
                                onClick={() => setPreviewFile(null)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            <img
                                src={getMediaUrl(previewFile.file)}
                                alt={previewFile.file.split('/').pop()}
                                className="max-w-full max-h-[70vh] object-contain mx-auto"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {filteredAttachments.length === 0 && (
                <div className="text-center py-12 ">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileImage className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay archivos</h3>
                    <p className="text-gray-500">
                        {searchTerm ? 'No se encontraron archivos que coincidan con tu búsqueda' : 'Aún no has subido ningún archivo'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default AttachmentsList;
